import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/feature/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import { useAppSettings } from "@/context/AppSettingsContext";
import { BRAND_NAME } from "@/lib/constants";

interface AdminTopbarProps {
  branchName: string | null;
}

export default function AdminTopbar({ branchName }: AdminTopbarProps) {
  const { profile, roleName, signOut } = useAuth();
  const { settings } = useAppSettings();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-background-200 bg-background-50/95 px-4 backdrop-blur md:px-6">
      <div className="flex items-center gap-3 lg:hidden">
        <BrandLogo size={34} />
        <span className="font-heading text-sm font-bold text-foreground-950">
          {settings.company_name || BRAND_NAME}
        </span>
      </div>

      <div className="hidden min-w-0 flex-1 items-center gap-2 lg:flex">
        <span className="flex h-5 w-5 items-center justify-center text-foreground-400">
          <i className="ri-map-pin-2-line text-base"></i>
        </span>
        <p className="truncate text-sm font-medium text-foreground-600">
          {branchName ?? "Sin sucursal asignada"}
        </p>
      </div>

      <div className="ml-auto flex items-center gap-2">
        <span className="hidden items-center gap-2 rounded-full border border-accent-300 bg-accent-50 px-3 py-1.5 text-xs font-semibold text-accent-900 sm:flex">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-shield-user-line"></i>
          </span>
          {roleName === "admin" ? "Administrador" : "Cajero"}
        </span>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex items-center gap-2 rounded-full border border-background-200 bg-background-100 py-1.5 pl-1.5 pr-3 transition-colors hover:bg-background-200"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-background-50">
              {(profile?.full_name?.[0] ?? "A").toUpperCase()}
            </span>
            <span className="hidden max-w-[140px] truncate text-sm font-semibold text-foreground-900 md:block">
              {profile?.full_name}
            </span>
            <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
              <i className="ri-arrow-down-s-line"></i>
            </span>
          </button>

          {menuOpen ? (
            <div className="animate-scale-in absolute right-0 mt-2 w-60 origin-top-right rounded-lg border border-background-200 bg-background-50 p-2">
              <div className="px-3 py-2">
                <p className="truncate text-sm font-semibold text-foreground-950">
                  {profile?.full_name} {profile?.last_name}
                </p>
                <p className="truncate text-xs text-foreground-500">{branchName ?? "Sin sucursal"}</p>
              </div>
              <div className="my-1 h-px bg-background-200" />
              <button
                type="button"
                onClick={handleSignOut}
                className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50"
              >
                <span className="flex h-5 w-5 items-center justify-center">
                  <i className="ri-logout-box-r-line text-base"></i>
                </span>
                Cerrar sesión
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  );
}